// https://stackoverflow.com/questions/27078285/simple-throttle-in-js
/**
 * Rate-limit a function to at most once per `wait` ms.
 * @param {number} wait - Milliseconds between invocations
 * @param {Function} func - Function to throttle
 * @param {{leading?: boolean, trailing?: boolean}} [options]
 * @returns {Function}
 */
function throttle(wait, func, options) {
    var context, args, result;
    var timeout = null;
    var previous = 0;
    if (!options) options = {};
    var later = function () {
        previous = options.leading === false ? 0 : Date.now();
        timeout = null;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
    };
    return function () {
        var now = Date.now();
        if (!previous && options.leading === false) previous = now;
        var remaining = wait - (now - previous);
        context = this;
        args = arguments;
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(context, args);
            if (!timeout) context = args = null;
        } else if (!timeout && options.trailing !== false) {
            timeout = setTimeout(later, remaining);
        }
        return result;
    };
}

/**
 * Delay function execution until `wait` ms after the last call.
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @param {boolean} [immediate] - Invoke on the leading edge instead of trailing
 * @returns {Function}
 */
function debounce(func, wait, immediate) {
    var timeout;
    return function () {
        var context = this, args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

/**
 * Returns true if the element's bottom edge is within the viewport.
 * @param {Element} el
 * @returns {boolean}
 */
function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
}

/**
 * Returns true if the keyboard event is the Escape key.
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
function isEscape(e) {
    // keyCode is getting deprecated use key instead when supported, which makes this messy
    var evt = e || window.event;
    var isEscape = false;
    if ("key" in evt) {
        isEscape = (evt.key == "Escape" || evt.key == "Esc");
    } else {
        isEscape = (evt.keyCode == 27);
    }
    return isEscape;
}

/**
 * Trap keyboard focus within `trap`. Adds a keydown listener — call once per open.
 * @param {Element} trap - Container to trap focus within
 * @param {boolean} [focusFirst=true] - Move focus to the first focusable element on call
 * @returns {Function} Function to remove the event listener when focus trap is no longer needed
 */
function trapFocus(trap, focusFirst = true) {
    const focusableElements = 'button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]), iframe';
    const firstFocusableElement = trap.querySelectorAll(focusableElements)[0]; // get first element to be focused inside trap
    const focusableContent = trap.querySelectorAll(focusableElements);
    const lastFocusableElement = focusableContent[focusableContent.length - 1]; // get last element to be focused inside trap
    const onKeyDown = function (e) {
        let isTabPressed = e.key === 'Tab' || e.keyCode === 9;

        if (!isTabPressed) {
            return;
        }

        if (e.shiftKey) { // if shift key pressed for shift + tab combination
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus(); // add focus for the last focusable element
                e.preventDefault();
            }
        } else { // if tab key is pressed
            if (document.activeElement === lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
                firstFocusableElement.focus(); // add focus for the first focusable element
                e.preventDefault();
            }
        }
    };
    document.addEventListener('keydown', onKeyDown);
    if (focusFirst) {
        setTimeout(() => {
            firstFocusableElement.focus();
        }, 350);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
}

/**
 * Format a cents integer as a dollar string, e.g. 1999 → "$19.99".
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
    const amount = cents / 100;
    return amount % 1 === 0
        ? `$${amount}`
        : `$${amount.toFixed(2)}`;
}

/**
 * Returns a price string, including ADA-friendly sale markup when
 * compareAtPrice is greater than price.
 */
function formatPrice(price, compareAtPrice = null) {
    if (compareAtPrice && compareAtPrice > price) {
        return `<s aria-hidden="true">${formatMoney(compareAtPrice)}</s> <span class="visually-hidden">Original price: ${formatMoney(compareAtPrice)}</span>${formatMoney(price)}`;
    }
    return formatMoney(price);
}

/**
 * Escape HTML special characters to prevent XSS in innerHTML contexts.
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// ---------------------------------------------------------------------------
// Loading states
// ---------------------------------------------------------------------------
/**
 * Show loading state on element
 * @param {HTMLElement} element - Element to show loading on
 */
function addWorking(element) {
    element.classList.add('working');
}

/**
 * Hide loading state on element
 * @param {HTMLElement} element - Element to hide loading on
 */
function removeWorking(element) {
    element.classList.remove('working');
}

/**
 * Shipping Progress
 *
 * Exports updateShippingProgress(totalCents) – updates every .shipping-progress
 * element on the page (cart page, cart panel, PDP, etc.).
 *
 * No event listeners here; callers are responsible for wiring events.
 * theme.js registers a passive cart:count:updated listener.
 */

const settings = window.cartSettings || {};
const threshold = settings.freeShippingThreshold || 0;
const overMessage = settings.freeShippingOverMessage || '';
const underMessage = settings.freeShippingUnderMessage || '';
const previewMessage = settings.freeShippingPreviewMessage || '';

/**
 * Build a free shipping message string for a given cart total.
 * @param {number} totalCents - Cart total in cents
 * @returns {string} HTML message string (may contain richtext markup)
 */
function getShippingMessage(totalCents, preview = false) {
    if (!threshold) return '';
    if (totalCents >= threshold) return overMessage;
    const remaining = threshold - totalCents;
    if (preview) {
        return previewMessage.replace('{amt}', formatMoney(remaining));
    }
    return underMessage.replace('{amt}', formatMoney(remaining));
}

/**
 * Apply shipping progress state to a single .shipping-progress element.
 * @param {Element} el - The .shipping-progress element to update
 * @param {number} totalCents - Cart total in cents
 * @param {boolean} [preview] - Use preview message copy
 */
function applyShippingProgress(el, totalCents, preview = false) {
    if (!threshold) return;
    const widthPercent = Math.min((totalCents / threshold) * 100, 100);
    const isFilled = totalCents >= threshold;
    const messageEl = el.querySelector('.shipping-progress__message');
    if (messageEl) messageEl.innerHTML = getShippingMessage(totalCents, preview);
    const fill = el.querySelector('.shipping-progress__fill');
    if (fill) {
        fill.style.width = `${widthPercent}%`;
        fill.classList.toggle('shipping-progress__fill--filled', isFilled);
    }
}

/**
 * Update all .shipping-progress elements on the page.
 * Skips bars marked data-live-preview (PDP owns those via product-form.js).
 * @param {number} totalCents - Cart total in cents
 */
function updateShippingProgress(totalCents) {
    if (!threshold) return;
    const elements = document.querySelectorAll('.shipping-progress:not([data-live-preview])');
    elements.forEach(el => applyShippingProgress(el, totalCents));
}

(function () {
/**
 * Quick Add
 *
 * Custom element wrapping the shared quick-add dialog.
 * Handles two flows:
 *   1. Simple (no variants): posts directly to Cart API, no dialog.
 *   2. Multi-variant: fetches product JSON, populates the dialog with
 *      option pickers, then opens it.
 *
 * Public API:
 *   quickAddDialog.open(product) — populate and show the dialog
 */
class QuickAddDialog extends HTMLElement {
    // Internal DOM refs — assigned in connectedCallback
    #dialog;
    #title;
    #price;
    #image;
    #productOptions;
    #variantInput;
    #form;
    #submitButton;
    #submitButtonText;

    #productCache = {};
    #currentProduct = null;
    #currentCartTotal = 0;

    connectedCallback() {
        // Seed cart total and keep it fresh
        fetch('/cart.js')
            .then(r => r.json())
            .then(cart => { this.#currentCartTotal = cart.total_price; })
            .catch(() => { });

        document.addEventListener('cart:count:updated', e => {
            this.#currentCartTotal = e.detail.total;
        });

        this.#dialog = this.querySelector('#quick-add');
        this.#title = this.querySelector('#quick-add-title');
        this.#price = this.querySelector('#quick-add-price');
        this.#productOptions = this.querySelector('#quick-add-options');
        this.#variantInput = this.querySelector('#quick-add-variant-id');
        this.#form = this.querySelector('#quick-add-form');
        this.#submitButton = this.querySelector('#quick-add-submit');
        this.#submitButtonText = this.querySelector('#quick-add-submit-text');

        // Option change — attached once here instead of inside #buildOptions,
        // preventing a listener leak on repeated opens.
        this.#productOptions.addEventListener('change', () => {
            if (this.#currentProduct) {
                this.#updateVariantState(this.#currentProduct);
                this.#previewShippingProgress();
            }
        });

        // Quantity +/- buttons
        this.#form.addEventListener('click', e => {
            const qtyBtn = e.target.closest('[data-qty-change]');
            if (!qtyBtn) return;
            e.preventDefault();
            const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
            const qtyInput = this.#form.querySelector('.quantity-input__value');
            if (!qtyInput) return;
            qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
            if (this.#currentProduct) {
                this.#updateVariantState(this.#currentProduct);
                this.#previewShippingProgress();
            }
        });

        // Live quantity typing
        this.#form.addEventListener('input', e => {
            if (!e.target.matches('.quantity-input__value')) return;
            if (this.#currentProduct) {
                this.#updateVariantState(this.#currentProduct);
                this.#previewShippingProgress();
            }
        });

        // Form submit
        this.#form.addEventListener('submit', async e => {
            e.preventDefault();
            const variantId = this.#variantInput.value;
            if (!variantId) return;
            await this.#addToCart(variantId);
            this.#dialog.hidePopover();
        });

        // Clear options when the dialog closes
        this.#dialog.addEventListener('toggle', e => {
            if (e.newState === 'closed') {
                this.#productOptions.innerHTML = '';
                this.#currentProduct = null;
            }
        });

        // Trigger buttons live outside this element — use document delegation
        document.addEventListener('click', async e => {
            // Multi-variant: open dialog
            const variantBtn = e.target.closest('[data-quick-add]');
            if (variantBtn) {
                const handle = variantBtn.dataset.productHandle;
                try {
                    variantBtn.disabled = true;
                    const product = await this.#fetchProduct(handle);
                    this.open(product);
                } catch (err) {
                    console.error(err);
                } finally {
                    variantBtn.disabled = false;
                }
                return;
            }

            // Simple: add directly without opening the dialog
            const simpleBtn = e.target.closest('[data-quick-add-simple]');
            if (simpleBtn) {
                const parent = simpleBtn.closest(simpleBtn.dataset.workingTarget);
                addWorking(parent);
                await this.#addToCart(simpleBtn.dataset.variantId);
                removeWorking(parent);
                return;
            }

            // Checkout now: add first available variant, then go to checkout (no cart drawer)
            const checkoutBtn = e.target.closest('[data-checkout-now]');
            if (checkoutBtn) {
                e.preventDefault();
                const variantId = checkoutBtn.dataset.variantId;
                if (!variantId) return;
                const parent = checkoutBtn.closest(checkoutBtn.dataset.workingTarget || '.product-card');
                addWorking(parent);
                checkoutBtn.disabled = true;
                try {
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
                    });
                    if (!res.ok) throw new Error('Cart add failed');
                    window.location.href = '/checkout';
                } catch (err) {
                    console.error(err);
                    checkoutBtn.disabled = false;
                    removeWorking(parent);
                }
            }
        });
    }

    // ---------------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------------

    /**
     * Populate and open the quick-add dialog for a product.
     * @param {Object} product - Shopify product JSON object
     */
    open(product) {
        this.#currentProduct = product;
        this.#title.textContent = product.title;
        this.#price.innerHTML = this.#moneyRange(product);

        // Reset quantity to 1 each time the dialog opens
        const qtyInput = this.#form.querySelector('.quantity-input__value');
        if (qtyInput) qtyInput.value = '1';

        this.#buildOptions(product);
        this.#updateVariantState(product);
        this.#previewShippingProgress();
        this.#dialog.showPopover();
    }

    // ---------------------------------------------------------------------------
    // Private methods
    // ---------------------------------------------------------------------------

    /**
     * Return a formatted price string for a product, noting price variance.
     * @param {Object} product - Shopify product JSON object
     * @returns {string}
     */
    #moneyRange(product) {
        if (product.price_varies) {
            return `Starting at ${formatMoney(product.price_min)}`;
        }
        const variant = product.variants.find(v => v.available) || product.variants[0];
        return formatPrice(variant.price, variant.compare_at_price);
    }

    /**
     * Append a Shopify CDN width param to an image URL.
     * @param {string} url
     * @param {number|null} [size] - Pixel width; defaults to the image element's width attribute
     * @returns {string}
     */
    #resizedImageUrl(url, size = null) {
        const width = size ?? this.#image.getAttribute('width');
        const param = url.includes('?') ? '&width=' : '?width=';
        return url + param + width;
    }

    /**
     * Fetch product JSON by handle, with in-memory caching.
     * @param {string} handle - Shopify product handle
     * @returns {Promise<Object>} Shopify product JSON object
     */
    async #fetchProduct(handle) {
        if (this.#productCache[handle]) return this.#productCache[handle];
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error(`Failed to load product: ${handle}`);
        const data = await res.json();
        this.#productCache[handle] = data;
        return data;
    }

    /**
     * Find the variant matching the checked option radios in the dialog.
     * Auto-selects the first available variant if nothing is checked yet.
     * @param {Object} product - Shopify product JSON object
     * @returns {Object|null}
     */
    #getSelectedVariant(product) {
        const options = [...this.#productOptions.querySelectorAll('[data-option-position]')];
        const selectedOptions = options.map(el => {
            const selected = el.querySelector('input[type="radio"]:checked, select');
            return selected ? selected.value : null;
        });

        if (!selectedOptions[0]) {
            const firstAvailableVariant = product.variants.find(variant => variant.available);
            // Select each option that the first available variant represents
            firstAvailableVariant?.options.forEach((option, index) => {
                const optionFieldset = this.#productOptions.querySelector(`[data-option-position="${index + 1}"]`);
                if (optionFieldset) {
                    const input = optionFieldset.querySelector(`input[value="${option}"]`);
                    if (input) input.checked = true;
                }
            });
            return firstAvailableVariant;
        }

        return product.variants.find(variant =>
            variant.options.every((option, index) => option === selectedOptions[index])
        ) || null;
    }

    /** Read the current quantity from the input, defaulting to 1. */
    #getQuantity() {
        return Math.max(1, parseInt(this.#form.querySelector('.quantity-input__value')?.value || 1, 10));
    }

    /** Preview free shipping progress for the current variant + quantity against the live cart total. */
    #previewShippingProgress() {
        const progressEl = this.#form.querySelector('.shipping-progress');
        if (!progressEl) return;
        const variant = this.#getSelectedVariant(this.#currentProduct);
        if (!variant) return;
        const previewTotal = this.#currentCartTotal + variant.price * this.#getQuantity();
        applyShippingProgress(progressEl, previewTotal, true);
    }

    /**
     * Sync the dialog's variant input, price, image, and submit button state.
     * @param {Object} product - Shopify product JSON object
     */
    #updateVariantState(product) {
        const variant = this.#getSelectedVariant(product);
        const qty = this.#getQuantity();
        if (variant) {
            this.#variantInput.value = variant.id;
            this.#submitButton.disabled = !variant.available;
            this.#submitButtonText.innerHTML = variant.available ? 'Add to cart&nbsp;&mdash;&nbsp;' : 'Sold out';

            this.#price.innerHTML = variant.available
                ? formatPrice(variant.price * qty, variant.compare_at_price ? variant.compare_at_price * qty : null)
                : '';
        } else {
            this.#variantInput.value = '';
            this.#submitButton.disabled = true;
            this.#submitButtonText.textContent = 'Unavailable';
            this.#price.textContent = '';
        }
    }

    /**
     * Build option picker fieldsets in the dialog from product JSON.
     * @param {Object} product - Shopify product JSON object
     */
    #buildOptions(product) {
        this.#productOptions.innerHTML = '';
        product.options.forEach(option => {
            const fieldset = document.createElement('fieldset');
            fieldset.className = 'option-group';
            fieldset.dataset.optionPosition = option.position;

            const legend = document.createElement('legend');
            legend.className = 'visually-hidden';
            legend.textContent = option.name;
            fieldset.appendChild(legend);

            const values = document.createElement('div');
            values.className = 'option-inputs';

            option.values.forEach((value, i) => {
                const id = `quick-add-option-${option.position}-${i}`;
                const variantWithImage = product.variants.find(
                    variant => variant.options[option.position - 1] === value && variant.featured_image
                );

                const input = document.createElement('input');
                input.type = 'radio';
                input.name = `option-${option.position}`;
                input.id = id;
                input.value = value;
                input.className = 'option-input visually-hidden';

                const optionName = document.createElement('span');
                optionName.className = 'option-label__name';
                optionName.textContent = value;

                const optionDetail = document.createElement('span');
                optionDetail.className = 'option-label__detail';
                optionDetail.textContent = window.optionLabels?.[product.variants.find(
                    variant => variant.options[option.position - 1] === value
                )?.id] || '';

                // Disable option if no variant with this value is available
                const isOptionAvailable = product.variants.some(
                    variant => variant.options[option.position - 1] === value && variant.available
                );
                if (!isOptionAvailable) {
                    input.disabled = true;
                    optionDetail.textContent = 'Out of stock';
                }

                const label = document.createElement('label');
                label.htmlFor = id;
                label.className = 'option-label';

                if (variantWithImage?.featured_image) {
                    const optionImage = document.createElement('img');
                    optionImage.className = 'option-label__image';
                    optionImage.alt = '';
                    optionImage.width = 60;
                    optionImage.height = 60;
                    optionImage.src = this.#resizedImageUrl(variantWithImage.featured_image.src, 60);
                    label.appendChild(optionImage);
                }
                label.appendChild(optionName);
                label.appendChild(optionDetail);

                values.appendChild(input);
                values.appendChild(label);
            });

            fieldset.appendChild(values);
            this.#productOptions.appendChild(fieldset);
        });
    }

    /**
     * POST a variant to the Cart API and fire cart update events.
     * @param {number|string} variantId - Shopify variant ID
     */
    async #addToCart(variantId) {
        addWorking(this.#dialog);
        try {
            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: this.#getQuantity() }),
            });
            if (!res.ok) throw new Error('Cart add failed');
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            document.dispatchEvent(new CustomEvent('cart:count:updated', {
                detail: { count: cart.item_count, total: cart.total_price }
            }));
            document.dispatchEvent(new CustomEvent('cart:item:added'));
        } catch (err) {
            console.error(err);
        } finally {
            removeWorking(this.#dialog);
        }
    }
}

customElements.define('quick-add-dialog', QuickAddDialog);
})();


(function () {
/**
 * Cart Panel
 *
 * Opens/closes the cart drawer panel.
 * - Fetches cart data on first open or hover (prefetch), caches it.
 * - Re-fetches on cart:updated.
 * - Prefetches on pointerenter for desktop.
 *
 * When #cart-panel is absent (e.g. Boost Cart enabled), the cart icon
 * opens Boost via window.Boost.Cart instead.
 */

// Custom cart drawer owns the cart UI when its section is enabled.
if (document.querySelector('[data-cart-drawer]')) return;

const panel = document.getElementById('cart-panel');
const body = document.getElementById('cart-panel-body');
const footer = document.getElementById('cart-panel-footer');

if (!panel) {
    function openBoostCart() {
        const cart = window.Boost?.Cart;
        if (cart?.open) {
            cart.refresh?.();
            cart.open();
            return true;
        }
        return false;
    }

    function openBoostCartWhenReady() {
        if (openBoostCart()) return;

        let attempts = 0;
        const timer = setInterval(() => {
            attempts += 1;
            if (openBoostCart() || attempts >= 25) clearInterval(timer);
        }, 100);
    }

    document.addEventListener('click', e => {
        if (!e.target.closest('[data-cart-panel-open]')) return;
        e.preventDefault();
        openBoostCartWhenReady();
    });

    document.addEventListener('cart:item:added', () => {
        openBoostCartWhenReady();
    });

    return;
}

let returnFocusTarget = null;
let releasePanelFocusTrap = null;
let cartData = null;   // cached cart response
let fetchPromise = null;   // in-flight fetch, prevents duplicate requests

// ---------------------------------------------------------------------------
// Cart fetch
// ---------------------------------------------------------------------------

/**
 * Fetch cart data from /cart.js. Deduplicates concurrent requests.
 * @returns {Promise<Object>} Shopify cart object
 */
async function fetchCart() {
    if (fetchPromise) return fetchPromise;
    fetchPromise = fetch('/cart.js')
        .then(response => { if (!response.ok) throw new Error('Cart fetch failed'); return response.json(); })
        .then(data => { cartData = data; fetchPromise = null; return data; })
        .catch(error => { fetchPromise = null; throw error; });
    return fetchPromise;
}

/** Clear the cart cache so the next open triggers a fresh fetch. */
function invalidateCart() {
    cartData = null;
    fetchPromise = null;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

/**
 * Render cart items and footer into the panel.
 * @param {Object} cart - Shopify cart object
 */
function renderCart(cart) {
    if (cart.item_count === 0) {
        body.innerHTML = '<div class="cart-panel__empty">Your cart is empty.</div>';
        footer.innerHTML = '';
        return;
    }

    const items = cart.items.map(item => `
        <div class="cart-panel-item" data-key="${item.key}">
            <div class="cart-panel-item__media">
            ${item.image
            ? `<img class="cart-panel-item__image" src="${item.image}" alt="${escapeHtml(item.title)}" width="100" height="100" loading="lazy">`
            : ''}
            </div>
            <div class="cart-panel-item__details">
                <div class="cart-panel-item__title">
                    <a class="cart-panel-item__link" href="${item.url}">${escapeHtml(item.product_title)}</a>
                </div>
                <div class="cart-panel-item__info">
                    ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-panel-item__variant">${escapeHtml(item.variant_title)}</div>`
            : ''}
                    <div class="cart-panel-item__price">${formatPrice(item.final_price, item.original_price > item.final_price ? item.original_price : null)}</div>
                    <div class="quantity-input">
                        <button class="quantity-input__control" data-qty-change="-1" data-key="${item.key}" aria-label="Decrease quantity"><svg viewBox="0 0 12 2" width="12" height="2" aria-hidden="true"> <use href="#icon-minus"></use> </svg></button>
                        <input class="quantity-input__value" type="text" name="updates[]" value="${item.quantity}" aria-live="polite" aria-label="quantity">
                        <button class="quantity-input__control" data-qty-change="1" data-key="${item.key}" aria-label="Increase quantity"><svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"> <use href="#icon-plus"></use> </svg></button>
                    </div>
                    
                </div>
                <div class="cart-panel-item__footer">
                    <div class="cart-panel-item__selling-plan">${item.selling_plan_allocation ? '<svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg"> <use href="#icon-subscription"></use> </svg>' + escapeHtml(item.selling_plan_allocation.selling_plan.name) : 'One time purchase'}</div>
                    <button class="cart-panel-item__remove" data-qty-change="0" data-key="${item.key}" aria-label="Remove item"><svg width="18" height="17" viewBox="0 0 18 17" fill="none" aria-hidden="true"> <use href="#icon-trash"></use> </svg></button>
                </div>
            </div>
        </div>
    `).join('');

    body.innerHTML = items;

    footer.innerHTML = `
        <table class="cart-panel__totals">
            <th scope="row">Subtotal</th>
            <td>${formatMoney(cart.total_price)}</td>
        </table>
        <a class="btn" href="/checkout">Checkout</a>
    `;

    releasePanelFocusTrap?.();
    releasePanelFocusTrap = trapFocus(panel, true);
}

/** Show a loading placeholder in the panel body while cart data is being fetched. */
function renderLoading() {
    body.innerHTML = '<div class="cart-panel__loading" aria-live="polite" aria-label="Loading cart">Loading…</div>';
    footer.innerHTML = '';
}

/** Show an error message in the panel body. */
function renderError() {
    body.innerHTML = '<div class="cart-panel__error">Unable to load cart. Please try again.</div>';
    footer.innerHTML = '';
}


// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

/**
 * Update only the cart panel's shipping progress bar.
 * Leaves product-form progress previews untouched.
 * @param {number} totalCents - Cart total in cents
 */
function updatePanelShippingProgress(totalCents) {
    const progressEl = panel.querySelector('.shipping-progress');
    if (!progressEl) return;
    applyShippingProgress(progressEl, totalCents);
}

/** Open the cart panel, fetch cart data if not cached, and render. */
async function openPanel() {
    returnFocusTarget = document.activeElement;
    panel.inert = false;
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-panel-open');

    requestAnimationFrame(() => {
        panel.querySelector('[data-cart-panel-close]')?.focus();
    });

    if (!cartData) {
        renderLoading();
        try {
            const cart = await fetchCart();
            renderCart(cart);
            updatePanelShippingProgress(cart.total_price);
        } catch {
            renderError();
        }
    } else {
        renderCart(cartData);
        updatePanelShippingProgress(cartData.total_price);
    }
}

/** Close the cart panel and restore focus to the triggering element. */
function closePanel() {
    panel.inert = true;
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-panel-open');
    releasePanelFocusTrap?.();
    releasePanelFocusTrap = null;
    returnFocusTarget?.focus();
    returnFocusTarget = null;
}

// ---------------------------------------------------------------------------
// Quantity change
// ---------------------------------------------------------------------------

/**
 * Update a line item's quantity via the Cart API and re-render the panel.
 * @param {string} key - Cart line item key
 * @param {number} delta - Amount to change by; 0 removes the item
 */
async function changeQty(key, delta) {
    const item = cartData?.items?.find(i => i.key === key);
    if (!item) return;
    const newQty = delta !== 0 ? item.quantity + delta : 0;
    const row = body.querySelector(`.cart-panel-item[data-key="${key}"]`);

    addWorking(row);

    try {
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQty }),
        });
        if (!response.ok) throw new Error('Cart change failed');
        cartData = await response.json();
        renderCart(cartData);
        releasePanelFocusTrap?.();
        releasePanelFocusTrap = trapFocus(panel, false);
        updateShippingProgress(cartData.total_price);
        document.dispatchEvent(new CustomEvent('cart:count:updated', { detail: { count: cartData.item_count, total: cartData.total_price } }));
    } catch (err) {
        console.error(err);
    } finally {
        removeWorking(row);
    }
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

document.addEventListener('click', async e => {
    if (e.target.closest('[data-cart-panel-open]')) {
        e.preventDefault();
        openPanel();
        return;
    }
    if (e.target.closest('[data-cart-panel-close]')) {
        closePanel();
        return;
    }
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
        const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
        const key = qtyBtn.dataset.key;
        await changeQty(key, delta);
    }
});

// Prefetch on hover (desktop)
document.addEventListener('pointerenter', e => {
    if (typeof e.target.closest === 'undefined') {
        return;
    }
    if (e.pointerType === 'mouse' && e.target.closest('[data-cart-panel-open]') && !cartData) {
        fetchCart().catch(() => { });
    }
}, true);

// Open + refresh after cart add
document.addEventListener('cart:item:added', async () => {
    invalidateCart();
    openPanel();
});

// Escape key
document.addEventListener('keydown', e => {
    if ((e.key === 'Escape' || e.key === 'Esc') && !panel.inert) {
        closePanel();
    }
});
})();


(function () {
/**
 * Search
 *
 * Focuses the search input when the search popover opens.
 */
const search = document.getElementById('site-search');
const searchInput = search.querySelector('input');
let releaseSearchFocusTrap = null;

search.addEventListener('toggle', e => {
    if (e.newState === 'open') {
        searchInput.focus();
        releaseSearchFocusTrap = trapFocus(search, false);
    } else {
        releaseSearchFocusTrap?.();
        releaseSearchFocusTrap = null;
    }
});
})();


(function () {
/**
 * Site Navigation
 */

const navigation = document.getElementById('site-navigation');
const openButton = document.querySelector('[data-navigation-open]');
const closeButton = document.querySelector('[data-navigation-close]');
const dropdownOpenButtons = document.querySelectorAll('[data-dropdown-open');
const dropdownCloseButtons = document.querySelectorAll('[data-dropdown-close]');
if (!navigation) throw new Error('site-navigation element not found');

let returnFocusTarget = null;
let releaseSubmenuFocusTrap = null;


// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

/** Open the mobile navigation drawer and save the return focus target. */
function open() {
    returnFocusTarget = document.activeElement;
    document.body.classList.add('site-navigation-open');
}

/** Close the mobile navigation drawer and restore focus. */
function close() {
    document.body.classList.remove('site-navigation-open');
    returnFocusTarget?.focus();
    returnFocusTarget = null;
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

openButton.addEventListener('click', e => {
    e.preventDefault();
    open();
    return;
});

closeButton.addEventListener('click', e => {
    e.preventDefault();
    close();
    return;
});

dropdownOpenButtons?.forEach(button => {
    button.addEventListener('click', e => {
        e.preventDefault();
        returnFocusTarget = document.activeElement;
        button.classList.add('active');
        button.setAttribute('aria-expanded', 'true');
        // on desktop, move focus to the drop down
        if (window.matchMedia('(min-width: 1025px)').matches) {
            button.closest('.primary-navigation__item').classList.add('active');
            const dropdown = button.nextElementSibling;
            if (dropdown) {
                releaseSubmenuFocusTrap = trapFocus(dropdown, true);
            }
        }
    });
});

dropdownCloseButtons?.forEach(button => {
    button.addEventListener('click', e => {
        e.preventDefault();
        dropdownOpenButtons?.forEach(el => {
            el.classList.remove('active');
            el.setAttribute('aria-expanded', 'false');
        });
    })
});

// Escape key
document.addEventListener('keydown', e => {
    if ((e.key === 'Escape' || e.key === 'Esc')) {
        close();
        dropdownOpenButtons?.forEach(el => {
            el.classList.remove('active');
            el.setAttribute('aria-expanded', 'false');
            el.closest('.primary-navigation__item').classList.remove('active');
        });
        releaseSubmenuFocusTrap?.();
        releaseSubmenuFocusTrap = null;
        returnFocusTarget?.focus();
        returnFocusTarget = null;
    }
});
})();


(function () {
/* Leaving around for reference, the hero doesn't support video
const isReduced = window.matchMedia(`(prefers-reduced-motion: reduce)`) === true || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;
if (!!isReduced) {
    const heroVideo = document.querySelector('.wp-block-pulp-hero video');
    if (heroVideo) {
        heroVideo.pause();
    }
}
*/

/*
Cart Badge Update
*/
/**
 * Update the cart icon badge count and aria-label.
 * @param {number} count - Current cart item count
 */
function updateCartBadge(count) {
    const cartButton = document.querySelector('[data-cart-panel-open]');
    if (!cartButton) return;

    let badge = cartButton.querySelector('.utilities__cart-count');
    if (count > 0) {
        if (!badge) {
            // Create badge if doesn't exist
            badge = document.createElement('span');
            badge.className = 'utilities__cart-count';
            badge.setAttribute('aria-hidden', 'true');
            cartButton.insertBefore(badge, cartButton.firstChild);
        }
        badge.textContent = count;

        // Update ARIA label
        const itemText = count === 1 ? 'item' : 'items';
        cartButton.setAttribute('aria-label', `Open cart, ${count} ${itemText}`);
    } else {
        // Remove badge if count is 0
        badge?.remove();
        cartButton.setAttribute('aria-label', 'Open cart');
    }
}

// Listen for count updates from all cart components
document.addEventListener('cart:count:updated', (e) => {
    updateCartBadge(e.detail.count);
    if (typeof e.detail.total === 'number') {
        updateShippingProgress(e.detail.total);
    }
});

// Debug: check active focus element
// document.addEventListener('focusin', (e) => {
//     console.log('Focused element:', e.target);
// });
})();
